export function textWidth(text, size, weight, context) {
    const width = context.measurer.measure(text, { ...context.base, fontSize: size, fontWeight: weight, italic: false }, Number.POSITIVE_INFINITY).width;
    // Bold glyphs are wider, and an estimating measurer does not know it. A little too much room
    // around a bold label is invisible; a label running out of its chip is not.
    return weight >= 600 ? width * 1.1 : width;
}
/** The text cut to fit, with an ellipsis. Binary search, because measuring is the cost. */
export function fitText(text, maxWidth, size, weight, context) {
    if (text === '' || textWidth(text, size, weight, context) <= maxWidth)
        return text;
    // Code points rather than code units, so a cut never splits an accented letter's surrogate pair.
    const characters = Array.from(text);
    let low = 0;
    let high = characters.length;
    while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        const candidate = `${characters.slice(0, middle).join('')}…`;
        if (textWidth(candidate, size, weight, context) <= maxWidth)
            low = middle;
        else
            high = middle - 1;
    }
    return low === 0 ? '' : `${characters.slice(0, low).join('').trimEnd()}…`;
}
export const move = (x, y) => ({ kind: 'move', to: { x, y } });
export const line = (x, y) => ({ kind: 'line', to: { x, y } });
/** A baseline for text whose visual middle should sit at `middle`. */
export function baseline(middle, size) {
    return middle + size * 0.35;
}
/**
 * A circular arc as cubic Béziers, at most a quarter turn each. Angles in radians, clockwise from
 * 3 o'clock. Starts where the pen already is: the caller moves there first.
 */
export function arc(cx, cy, r, from, to) {
    const segments = [];
    if (from === to || r <= 0)
        return segments;
    const steps = Math.max(1, Math.ceil(Math.abs(to - from) / (Math.PI / 2)));
    const step = (to - from) / steps;
    const k = (4 / 3) * Math.tan(step / 4);
    for (let index = 0; index < steps; index += 1) {
        const a = from + index * step;
        const b = a + step;
        const start = { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
        const end = { x: cx + r * Math.cos(b), y: cy + r * Math.sin(b) };
        segments.push({
            kind: 'cubic',
            control1: { x: start.x - k * r * Math.sin(a), y: start.y + k * r * Math.cos(a) },
            control2: { x: end.x + k * r * Math.sin(b), y: end.y - k * r * Math.cos(b) },
            to: end,
        });
    }
    return segments;
}
/** The point on a circle at an angle. */
export function polar(cx, cy, r, angle) {
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}
/**
 * A bar with its far end rounded and its base square — the base sits on the baseline, and a
 * rounded base would float. `end` names the side that is rounded.
 */
export function roundedBar(rect, radius, end) {
    const { x, y, width: w, height: h } = rect;
    const r = Math.max(0, Math.min(radius, w / 2, h / 2));
    const k = r * 0.5523;
    if (r === 0)
        return [move(x, y), line(x + w, y), line(x + w, y + h), line(x, y + h), line(x, y)];
    switch (end) {
        case 'top':
            return [
                move(x, y + h),
                line(x, y + r),
                {
                    kind: 'cubic',
                    control1: { x, y: y + r - k },
                    control2: { x: x + r - k, y },
                    to: { x: x + r, y },
                },
                line(x + w - r, y),
                {
                    kind: 'cubic',
                    control1: { x: x + w - r + k, y },
                    control2: { x: x + w, y: y + r - k },
                    to: { x: x + w, y: y + r },
                },
                line(x + w, y + h),
                line(x, y + h),
            ];
        case 'bottom':
            return [
                move(x, y),
                line(x + w, y),
                line(x + w, y + h - r),
                {
                    kind: 'cubic',
                    control1: { x: x + w, y: y + h - r + k },
                    control2: { x: x + w - r + k, y: y + h },
                    to: { x: x + w - r, y: y + h },
                },
                line(x + r, y + h),
                {
                    kind: 'cubic',
                    control1: { x: x + r - k, y: y + h },
                    control2: { x, y: y + h - r + k },
                    to: { x, y: y + h - r },
                },
                line(x, y),
            ];
        case 'right':
            return [
                move(x, y),
                line(x + w - r, y),
                {
                    kind: 'cubic',
                    control1: { x: x + w - r + k, y },
                    control2: { x: x + w, y: y + r - k },
                    to: { x: x + w, y: y + r },
                },
                line(x + w, y + h - r),
                {
                    kind: 'cubic',
                    control1: { x: x + w, y: y + h - r + k },
                    control2: { x: x + w - r + k, y: y + h },
                    to: { x: x + w - r, y: y + h },
                },
                line(x, y + h),
                line(x, y),
            ];
        case 'left':
            return [
                move(x + w, y),
                line(x + w, y + h),
                line(x + r, y + h),
                {
                    kind: 'cubic',
                    control1: { x: x + r - k, y: y + h },
                    control2: { x, y: y + h - r + k },
                    to: { x, y: y + h - r },
                },
                line(x, y + r),
                {
                    kind: 'cubic',
                    control1: { x, y: y + r - k },
                    control2: { x: x + r - k, y },
                    to: { x: x + r, y },
                },
                line(x + w, y),
            ];
    }
}
//# sourceMappingURL=primitives.js.map